// Sentry initialization for error tracking and performance monitoring
// https://docs.sentry.io/platforms/javascript/guides/react/

import * as Sentry from "@sentry/react";

// Only initialize Sentry if DSN and environment are configured
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

export function initializeSentry() {
  if (!SENTRY_DSN) {
    console.log('Sentry DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: ENVIRONMENT === 'development',

    // Session Replay
    replaysOnErrorSampleRate: 1.0, // 100% of errors get replayed
    replaysSessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 0, // 10% in prod, 0% in dev

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text and media by default for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Better error filtering
    beforeSend(event, hint) {
      // Filter out network errors that are expected during auth refresh
      if (hint.originalException) {
        const error = hint.originalException as any;
        const errorMessage = error?.message || '';
        const errorString = error?.toString() || '';

        // Filter out common network errors that are handled by retry logic
        if (
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Load failed')
        ) {
          // Check if this is a 401 that will be retried
          if (errorMessage.includes('401') || errorString.includes('401')) {
            // Don't send to Sentry as this is handled by auth refresh
            return null;
          }

          // For other network errors, add context
          event.tags = {
            ...event.tags,
            error_type: 'network_error',
          };
          event.fingerprint = ['network-error', errorMessage];
        }

        // Filter out messaging errors that appear to be transient
        if (
          errorMessage.includes('tx_attempts_exceeded') ||
          errorMessage.includes('tx_ack_timeout')
        ) {
          // These are likely WebSocket/messaging timeouts
          event.tags = {
            ...event.tags,
            error_type: 'messaging_timeout',
          };
          event.level = 'warning';
          event.fingerprint = ['messaging-timeout'];
        }
      }

      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'Non-Error promise rejection captured',
      // Random layout errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Auth errors that are handled
      'UnauthenticatedUser',
      'UNAUTHENTICATED',
      // Vite HMR in development
      'Importing a module script failed',
    ],
  });
}
